import React, { useState } from 'react';
import {
    Card,
    CircularLoader,
    Divider,
    AlertBar,
} from '@dhis2/ui';
import { useDataQuery } from '@dhis2/app-runtime';
import {
    PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
    LineChart, Line, XAxis, YAxis, CartesianGrid,
    BarChart, Bar, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from 'recharts';
import { useIndicators, useOrgUnit, useDataElements } from './hooks/useData';
import Objectives from './components/Objectives'
import RealTasks from './components/RealTasks'
import RealGoals from './components/RealGoals'
import TaskMetrics from './components/TaskMetrics'
import LeadershipDashboard from './components/LeadershipDashboard'
import ReviewReminders from './components/ReviewReminders'
import KPIKRAReports from './components/KPIKRAReports';
import { usePerformanceHierarchy } from './hooks/usePerformanceHierarchy'; // Add this import

const H4 = ({ children, style }) => (
    <div style={{ fontSize: 24, fontWeight: '700', marginBottom: 24, color: '#212529', ...style }}>
        {children}
    </div>
);
const H5 = ({ children, style }) => (
    <div style={{ fontSize: 20, fontWeight: '600', marginBottom: 16, color: '#343a40', ...style }}>
        {children}
    </div>
);
const BodyText = ({ children, style }) => (
    <div style={{ fontSize: 14, color: '#555', lineHeight: 1.5, ...style }}>
        {children}
    </div>
);

const MetricCard = ({ title, value, subtitle, color = '#007bff' }) => (
    <Card style={{ padding: 16, textAlign: 'center', height: '100%' }}>
        <div style={{ fontSize: 32, fontWeight: '700', color: color, marginBottom: 8 }}>
            {value}
        </div>
        <div style={{ fontSize: 16, fontWeight: '600', color: '#343a40', marginBottom: 4 }}>
            {title}
        </div>
        {subtitle && (
            <div style={{ fontSize: 12, color: '#6c757d' }}>
                {subtitle}
            </div>
        )}
    </Card>
);

const statusColors = {
    'On Track': '#28a745',
    'At Risk': '#ffc107',
    'Behind Schedule': '#dc3545',
};

const DashboardPage = ({ userHasCompanyAccess = true }) => {
    // Step 1: Get current user's organization units
    const { 
        loading: userLoading, 
        error: userError, 
        data: userData 
    } = useDataQuery({
        me: {
            resource: 'me',
            params: {
                fields: 'id,displayName,organisationUnits[id,name,path,level]'
            }
        }
    });

    // Step 2: Extract the user's actual organization unit
    const userOrgUnits = userData?.me?.organisationUnits || [];
    const [selectedOrgUnitId, setSelectedOrgUnitId] = useState(userOrgUnits[0]?.id);
    
    const userOrgUnitId = selectedOrgUnitId || userOrgUnits[0]?.id;
    const userOrgUnitName = userOrgUnits.find(ou => ou.id === userOrgUnitId)?.name;
    const userName = userData?.me?.displayName;

    // Step 3: Fetch performance data once and share between components
    const { 
        loading: performanceLoading, 
        error: performanceError, 
        data: performanceData 
    } = usePerformanceHierarchy(userOrgUnitId);

    // Step 4: Fetch organization-specific data
    const { 
        loading: orgLoading, 
        error: orgError, 
        data: orgData 
    } = useOrgUnit(userOrgUnitId);
    
    // Fetch available indicators
    const { 
        loading: indicatorsLoading, 
        error: indicatorsError, 
        data: indicatorsData 
    } = useIndicators();

    const topCardBgColors = ['#f3fbf5ff', '#f7f0e4ff', '#ebdcfaff'];

    
    // Add this debug section to see what indicators you have
    React.useEffect(() => {
        if (indicatorsData && indicatorsData.indicators) {
            console.log('Available indicators:', indicatorsData.indicators);
        }
    }, [indicatorsData]);

    const { 
        loading: dataElementsLoading, 
        error: dataElementsError, 
        data: dataElementsData 
    } = useDataElements();

    // And add this debug effect
    React.useEffect(() => {
        if (dataElementsData && dataElementsData.dataElements) {
            console.log('Available data elements:', dataElementsData.dataElements);
        }
    }, [dataElementsData]);

    // Add debug logging to verify user and org unit
    React.useEffect(() => {
        if (userData?.me) {
            console.log('👤 Current User:', userName);
            console.log('🏢 User Organization Units:', userOrgUnits);
            console.log('🎯 Using Org Unit ID:', userOrgUnitId);
            console.log('🏢 Using Org Unit Name:', userOrgUnitName);
        }
    }, [userData, userOrgUnitId, userOrgUnitName, userName, userOrgUnits]);

    // Debug performance data
    React.useEffect(() => {
        if (performanceData) {
            console.log('📊 Performance Data Available:', {
                objectives: performanceData.objectives?.events?.length,
                tasks: performanceData.tasks?.events?.length,
                positions: performanceData.positions?.trackedEntityInstances?.length,
                staff: performanceData.staff?.trackedEntityInstances?.length
            });
        }
    }, [performanceData]);

    // Show loading while fetching user data
    if (userLoading) {
        return (
            <div style={{ padding: 24 }}>
                <CircularLoader small />
                <BodyText style={{ textAlign: 'center', marginTop: 16 }}>
                    Loading your dashboard...
                </BodyText>
            </div>
        );
    }

    if (userError || !userOrgUnitId) {
        return (
            <div style={{ padding: 24 }}>
                <AlertBar critical>
                    {userError?.message || 'Unable to determine your organization unit. Please contact administrator.'}
                </AlertBar>
                {userData?.me?.organisationUnits && (
                    <div style={{ marginTop: 16 }}>
                        <strong>Your accessible organizations:</strong>
                        <ul>
                            {userData.me.organisationUnits.map(ou => (
                                <li key={ou.id}>{ou.name} (ID: {ou.id})</li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        );
    }

    if (orgLoading || indicatorsLoading || performanceLoading) {
        return (
            <div style={{ padding: 24 }}>
                <CircularLoader small />
                <BodyText style={{ textAlign: 'center', marginTop: 16 }}>
                    Loading dashboard data for {userOrgUnitName}...
                </BodyText>
            </div>
        );
    }

    if (orgError || indicatorsError || performanceError) {
        return (
            <div style={{ padding: 24 }}>
                <AlertBar critical>
                    Error loading dashboard data: {orgError?.message || indicatorsError?.message || performanceError?.message}
                </AlertBar>
            </div>
        );
    }

    return (
        <div style={{ padding: 14, marginTop: '1px', fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif' }}>
            {/* Organisation Unit Header */}
            <div style={{ display: 'flex', color: 'green', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3>Performance Management Dashboard</h3>
                <div style={{ textAlign: 'right' }}>
                    <h3 style={{ color: '#054382ff', margin: 0 }}>{userOrgUnitName}</h3>
                    <div style={{ fontSize: '0.9rem', color: '#666' }}>
                        Welcome, {userName}
                    </div>
                </div>
            </div>
                       
            
            <hr style={{ width: '100%', border: '1.5px solid #c0c3c5ff', margin: '10px 0 24px 0' }} />

            {/* Organisation Unit Info */}
            <ReviewReminders orgUnitId={userOrgUnitId} />

            <div style={{ display: 'flex', color: 'green', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, marginTop: '20px' }}> 
                <h3>Goals Section</h3>
            </div>
            <hr style={{ width: '100%', border: '1px solid #e0e0e0', margin: '10px 0 24px 0' }} />

            <RealGoals 
            orgUnitId={userOrgUnitId} 
            performanceData={performanceData}
            />

            <div style={{ display: 'flex', color: 'green', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, marginTop: '20px' }}> 
                <h3>Objectives Section</h3>
            </div>
            <hr style={{ width: '100%', border: '1px solid #e0e0e0', margin: '10px 0 24px 0' }} />

            {/* Updated Objectives component with current user and performance data */}
            <Objectives 
                orgUnitId={userOrgUnitId}
                currentUser={{ name: userName, id: userData?.me?.id }}
                performanceData={performanceData}
            />

            <div style={{ display: 'flex', color: 'green', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, marginTop: '20px' }}> 
                <h3>Key Performance Indicators Section</h3>
            </div>
            <hr style={{ width: '100%', border: '1px solid #e0e0e0', margin: '10px 0 24px 0' }} />

            <KPIKRAReports orgUnitId={userOrgUnitId} /> 
            
            <div style={{ display: 'flex', color: 'green', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}> 
                <h3>Tasks Section</h3>
            </div>
            <hr style={{ width: '100%', border: '1px solid #e0e0e0', margin: '10px 0 24px 0' }} /> 
            
            <div style={{ marginTop: 24 }}>
                {/* Updated RealTasks component with performance data */}
                <RealTasks 
                    orgUnitId={userOrgUnitId}
                    performanceData={performanceData}
                />
            </div>
            
            <TaskMetrics orgUnitId={userOrgUnitId} />

            <div style={{ display: 'flex', color: 'green', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}> 
                <h3>Leadership and Behaviors Section</h3>
            </div>
            <hr style={{ width: '100%', border: '1px solid #e0e0e0', margin: '10px 0 24px 0' }} />

            <div style={{ display: 'grid', marginTop: '10px', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
                <LeadershipDashboard orgUnitId={userOrgUnitId} />
            </div>

            {/* Row 1: Three cards in a row */}
            <div style={{ display: 'flex', flexWrap: 'nowrap', justifyContent: 'space-between', gap: '24px', marginBottom: 40 }}>
            </div>
        </div>
    );
};

export default DashboardPage;